import { useEffect, useState } from "react";
import { Toaster, toast } from "sonner";
import "@/App.css";
import { api, errMsg } from "@/lib/api";
import usePoll from "@/hooks/usePoll";
import Header from "@/components/panel/Header";
import StatusStrip from "@/components/panel/StatusStrip";
import SetupGuide from "@/components/panel/SetupGuide";
import GuildSwitcher from "@/components/panel/GuildSwitcher";
import NowPlaying from "@/components/panel/NowPlaying";
import AddSong from "@/components/panel/AddSong";
import QueueList from "@/components/panel/QueueList";
import History from "@/components/panel/History";
import LavalinkCard from "@/components/panel/LavalinkCard";
import CommandMatrix from "@/components/panel/CommandMatrix";

export default function App() {
  const { data: status } = usePoll(api.status, 5000);
  const online = !!status?.online;
  const { data: guilds } = usePoll(api.guilds, 10000, online);
  const [gid, setGid] = useState(null);
  const { data: guild, setData: setGuild } = usePoll(() => api.guild(gid), 2500, online && !!gid, [gid]);
  const { data: history } = usePoll(() => api.history(gid || undefined), 8000, true, [gid]);

  useEffect(() => {
    if (!gid && guilds?.length) setGid(guilds[0].id);
  }, [guilds, gid]);

  const act = async (kind, ...args) => {
    try {
      const player = await api[kind](gid, ...args);
      setGuild((g) => (g ? { ...g, player } : g));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const onPlay = async (body) => {
    try {
      const r = await api.play(gid, body);
      toast.success(r.type === "playlist" ? `Dodano playlistę ${r.name} (${r.count})` : `Dodano: ${r.track.title}`);
      setGuild(await api.guild(gid));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="min-h-screen grain" data-testid="dashboard">
      <Toaster theme="dark" position="bottom-right" richColors />
      <Header status={status} />
      <main className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
        <StatusStrip status={status} />
        {online ? (
          <GuildSwitcher guilds={guilds} selected={gid} onSelect={setGid} />
        ) : (
          status && <SetupGuide status={status} />
        )}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <NowPlaying player={guild?.player} act={act} />
          <section className="card p-5 sm:p-6 lg:col-span-5 space-y-6 fade-up" data-testid="queue-card">
            <div>
              <div className="caption mb-3">{online ? "Dodaj utwór" : "Wyszukiwarka (podgląd)"}</div>
              <AddSong guild={guild} canPlay={online && !!gid} onPlay={onPlay} />
            </div>
            <QueueList player={guild?.player} act={act} />
          </section>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <History items={history} />
          <LavalinkCard lavalink={status?.lavalink} />
        </div>
        <CommandMatrix prefix={status?.prefix} />
        <footer className="text-center text-xs font-mono text-slate-600 pb-6">Lavalink v4 · discord.py · wavelink</footer>
      </main>
    </div>
  );
}
